import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, Briefcase, Plane, CheckCircle2, Sparkles, FileText } from "lucide-react";
import { API } from "@/lib/api";

const TEASERS = {
  billing: {
    icon: FileText,
    title: "Business Studio Suite",
    tagline: "Professionell anbieten & abrechnen – das Fundament.",
    bullets: [
      "Angebote & Rechnungen unbegrenzt, mit deinem Logo",
      "Online-Zahlung (Stripe) & Zahlungs-QR-Code",
      "Pro: E-Rechnung (ZUGFeRD/XRechnung) & DATEV-Export",
      "Business: Team, Mahnlauf & Priority Support",
    ],
  },
  crm: {
    icon: Briefcase,
    title: "CRM Studio",
    tagline: "Aus Kontakten werden Kunden. Aus Kunden wird Umsatz.",
    bullets: [
      "Deal-Pipeline vom Lead bis zum Abschluss",
      "Kundenakten mit kompletter Historie",
      "Aus dem Deal mit einem Klick zum Angebot",
      "Premium: Vertriebsstatistik & Aufgaben-Automation",
    ],
  },
  travel: {
    icon: Plane,
    title: "Business Travel Studio",
    tagline: "Geschäftsreisen planen, genehmigen, abrechnen – in Minuten.",
    bullets: [
      "Genehmigungs-Workflow für dein Team",
      "Vergleichsrechner: Auto vs. Bahn vs. Flug",
      "Verpflegungspauschalen automatisch berechnet",
      "Premium: KI-Belegerkennung per Foto",
    ],
  },
};

export const LockedTab = ({ label, icon: Icon, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    title="In deinem Tarif nicht enthalten – klicke zum Freischalten"
    className="inline-flex items-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-[var(--brand-muted)] opacity-60 hover:opacity-100 hover:text-[var(--brand-ink)] transition-opacity"
  >
    <Icon className="w-4 h-4 mr-1" /> {label} <Lock className="w-3 h-3 ml-1.5" />
  </button>
);

export function ModuleUpsellDialog({ moduleId, onClose, onUpgrade }) {
  const [modules, setModules] = useState([]);
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    if (moduleId === "billing") {
      axios.get(`${API}/billing/plans`).then((r) => setPlans(r.data || [])).catch(() => {});
    } else if (moduleId) {
      axios.get(`${API}/billing/modules`).then((r) => setModules(r.data || [])).catch(() => {});
    }
  }, [moduleId]);
  const teaser = TEASERS[moduleId];
  if (!moduleId || !teaser) return null;
  const mod = modules.find((m) => m.id === moduleId);
  const fromPrice = moduleId === "billing" ? plans[0]?.month : mod?.tiers?.[0]?.month;
  const Icon = teaser.icon;
  return (
    <Dialog open={!!moduleId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0" data-testid="module-upsell-dialog">
        <DialogTitle className="sr-only">{teaser.title} freischalten</DialogTitle>
        <div className="bg-[var(--brand-ink)] px-6 py-6 text-white">
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest bg-white/10 rounded-full px-3 py-1 mb-3" data-testid="module-upsell-hint">
            <Lock className="w-3 h-3" /> In deinem Tarif nicht enthalten
          </span>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[var(--brand-primary)] flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-[Playfair_Display] text-xl font-bold leading-tight">{teaser.title}</h2>
              <p className="text-sm text-white/70">{teaser.tagline}</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-5">
          <ul className="space-y-2.5 mb-5">
            {teaser.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-[var(--brand-ink)]">
                <CheckCircle2 className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 shrink-0" /> {b}
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between rounded-xl border border-[var(--brand-border)] bg-[var(--brand-bg)] px-4 py-3 mb-5">
            <span className="text-sm text-[var(--brand-muted)]">3 Pakete zur Wahl</span>
            <span className="text-sm font-semibold text-[var(--brand-ink)]">
              {fromPrice != null ? `ab ${fromPrice.toLocaleString("de-DE")} €/Monat` : "flexible Pakete"}
            </span>
          </div>
          <Button onClick={onUpgrade} className="w-full rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="module-upsell-cta">
            <Sparkles className="w-4 h-4 mr-1.5" /> Jetzt freischalten
          </Button>
          <p className="text-center text-[11px] text-[var(--brand-muted)] mt-3">Monatlich kündbar · zzgl. MwSt.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
